import * as Sentry from "@sentry/react";
import {
  getSentryDSN,
  getTracesSampleRate,
  getReplaySampleRate,
  getTenant,
  beforeSend,
  isSentryEnabled,
  detectEnvironment,
} from "./sentryConfig.js";

/**
 * Initialize Sentry for React application
 * All configuration is managed in sentryConfig.js
 */
export const initSentry = () => {
  try {
    if (!isSentryEnabled()) {
      console.log("Sentry is disabled or DSN not configured, skipping initialization");
      return;
    }

    const environment = detectEnvironment();
    const dsn = getSentryDSN();

    console.log(`Initializing Sentry for environment: ${environment}`);

    Sentry.init({
      dsn,
      environment,
      tracesSampleRate: getTracesSampleRate(),
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      replaysSessionSampleRate: getReplaySampleRate(),
      replaysOnErrorSampleRate: 1.0,
      sendDefaultPii: true,
      beforeSend,
      initialScope: {
        tags: {
          tenant: getTenant(),
          hostname: window.location.hostname,
        },
      },
    });
  } catch (err) {
    console.warn("Sentry initialization failed (app continues):", err);
  }
};

/**
 * Manually capture an exception
 * @param {Error} error - Error object to capture
 * @param {Object} context - Additional context
 */
export const captureException = (error, context = {}) => {
  try {
    if (!isSentryEnabled()) return;

    Sentry.captureException(error, {
      extra: context,
    });
  } catch (err) {
    console.warn("Sentry captureException failed:", err);
  }
};

/**
 * Manually capture a message
 * @param {string} message - Message to capture
 * @param {string} level - Severity level (error, warning, info, debug)
 * @param {Object} context - Additional context
 */
export const captureMessage = (message, level = "info", context = {}) => {
  try {
    if (!isSentryEnabled()) return;

    Sentry.captureMessage(message, {
      level,
      extra: context,
    });
  } catch (err) {
    console.warn("Sentry captureMessage failed:", err);
  }
};

/**
 * Set user context
 * @param {Object} user - User object with id, email, username, etc.
 */
export const setUser = (user) => {
  try {
    if (!isSentryEnabled()) return;

    Sentry.setUser(user);
  } catch (err) {
    console.warn("Sentry setUser failed:", err);
  }
};

/**
 * Clear user context
 */
export const clearUser = () => {
  try {
    if (!isSentryEnabled()) return;

    Sentry.setUser(null);
  } catch (err) {
    console.warn("Sentry clearUser failed:", err);
  }
};

/**
 * Add breadcrumb
 * @param {Object} breadcrumb - Breadcrumb data
 */
export const addBreadcrumb = (breadcrumb) => {
  try {
    if (!isSentryEnabled()) return;

    Sentry.addBreadcrumb(breadcrumb);
  } catch (err) {
    console.warn("Sentry addBreadcrumb failed:", err);
  }
};

/**
 * Set custom tag
 * @param {string} key - Tag key
 * @param {string} value - Tag value
 */
export const setTag = (key, value) => {
  try {
    if (!isSentryEnabled()) return;

    Sentry.setTag(key, value);
  } catch (err) {
    console.warn("Sentry setTag failed:", err);
  }
};

/**
 * Set custom context
 * @param {string} name - Context name
 * @param {Object} context - Context data
 */
export const setContext = (name, context) => {
  try {
    if (!isSentryEnabled()) return;

    Sentry.setContext(name, context);
  } catch (err) {
    console.warn("Sentry setContext failed:", err);
  }
};

// Export Sentry for advanced usage
export { Sentry };
